import type { UserCoupon } from "@/app/actions/coupons";
import type {
  ExchangeRates,
  SupportedCurrency,
} from "@/lib/utils/currency";
import {
  convertCurrency,
  formatPriceSimple,
  roundForCurrency,
} from "@/lib/utils/currency";
import { parseDateOnly } from "@/lib/reservation-utils";

/**
 * 쿠폰 계산/포맷 유틸 (native currency + FX 변환 인식).
 *
 * 쿠폰은 단일 native currency 기준 저장 (platform=KRW, partner=partner.default_currency).
 * 결제/표시 시점에 환율로 변환되어 사용됨. 이 유틸은 native 값 + 현재 환율 rates로
 * 임의의 target currency 기준 미리보기를 계산합니다.
 *
 * ★ 환율 정책: 쿠폰 변환은 스프레드 없는 순수 시장 환율(`marketRate`)을 사용한다.
 *    상품 결제 금액은 스프레드 적용된 `rate`를 써도, 쿠폰은 face value에 맞춰야
 *    플랫폼이 할인 구간에서 스프레드만큼 추가 손실을 입지 않는다. 따라서 모든
 *    convertCurrency 호출에 `{ useMarketRate: true }` 플래그를 붙인다.
 *
 * 서버 권위 검증은 validate_coupon_code RPC(환율 변환 내장)로 수행되고,
 * Hold 시 native + fx_rate_date가 coupon_holds에 스냅샷되어 정산 정확도를 보장합니다.
 */
const COUPON_FX_OPTIONS = { useMarketRate: true } as const;

/**
 * 쿠폰 계산에 필요한 필드만 추린 인터페이스 (UserCoupon, AvailableCoupon 공용)
 */
export interface CouponCalcInput {
  discount_type: "fixed" | "percentage";
  native_currency: SupportedCurrency;
  native_discount_value: number; // fixed: amount, percentage: RATE
  native_max_discount: number | null; // percentage 상한 (native)
  native_min_order: number;
}

/** coupons 행 중 계산에 쓰는 컬럼. */
export interface CouponDefinitionForCalc {
  coupon_type: string | null;
  partner_id: string | null;
  discount_type: string;
  discount_values: Record<string, number | string | null> | null;
  max_discount_amounts: Record<string, number | string | null> | null;
  min_order_amounts: Record<string, number | string | null> | null;
}

/**
 * coupons 행 → CouponCalcInput. getUserCoupons(actions/coupons.ts)가 브라우저용
 * 목록에 native_* 를 채우는 규칙과 같다 (서버 partnerDeduction 계산용):
 * 플랫폼(또는 partner_id 없음) = KRW, 파트너 = 파트너 기본 통화(없으면 KRW).
 */
export function couponCalcInputFromDefinition(
  coupon: CouponDefinitionForCalc,
  partnerDefaultCurrency: string | null | undefined,
): CouponCalcInput {
  const nativeCurrency = (
    coupon.coupon_type === "platform" || !coupon.partner_id
      ? "KRW"
      : partnerDefaultCurrency || "KRW"
  ) as SupportedCurrency;
  const values = coupon.discount_values ?? {};
  const maxes = coupon.max_discount_amounts ?? {};
  const minOrders = coupon.min_order_amounts ?? {};
  const isPercentage = coupon.discount_type === "percentage";
  return {
    discount_type: isPercentage ? "percentage" : "fixed",
    native_currency: nativeCurrency,
    native_discount_value: isPercentage
      ? Number(values.RATE || 0)
      : Number(values[nativeCurrency] || 0),
    native_max_discount: isPercentage ? Number(maxes[nativeCurrency] || 0) || null : null,
    native_min_order: Number(minOrders[nativeCurrency] || 0),
  };
}

function getMinRemainder(currency: SupportedCurrency): number {
  switch (currency) {
    case "KRW":
      return 100;
    case "JPY":
      return 10;
    case "USD":
    case "EUR":
      return 0.1;
    case "CNY":
      return 0.5;
    default:
      return 100;
  }
}

export function formatCouponAmount(
  amount: number,
  currency: SupportedCurrency,
): string {
  const rounded = roundForCurrency(amount, currency);
  return formatPriceSimple(rounded, currency);
}

export function formatCouponDate(dateStr: string): string {
  const d = parseDateOnly(dateStr);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}.${mm}.${dd}`;
}

/**
 * 최소 주문 금액을 order currency로 변환.
 */
export function convertMinOrder(
  coupon: CouponCalcInput,
  orderCurrency: SupportedCurrency,
  rates: Partial<ExchangeRates>,
): number {
  if (coupon.native_min_order <= 0) return 0;
  if (coupon.native_currency === orderCurrency) return coupon.native_min_order;
  const converted = convertCurrency(
    coupon.native_min_order,
    coupon.native_currency,
    orderCurrency,
    rates,
    COUPON_FX_OPTIONS,
  );
  return roundForCurrency(converted, orderCurrency);
}

/**
 * 최소 주문 금액 충족 여부 (FX 변환 포함)
 */
export function isCouponApplicable(
  coupon: CouponCalcInput,
  totalPrice: number,
  orderCurrency: SupportedCurrency,
  rates: Partial<ExchangeRates>,
): boolean {
  return totalPrice >= convertMinOrder(coupon, orderCurrency, rates);
}

/**
 * 환율 데이터가 아직 로드되지 않았는지 체크 (빈 객체면 로딩 중)
 */
export function areRatesLoaded(rates: Partial<ExchangeRates>): boolean {
  return Object.keys(rates).length > 0;
}

/**
 * 발급 쿠폰(coupon_issues) 만료 여부.
 *
 * `coupon_issues.status`를 expired로 전환하는 배치/cron이 없어 만료돼도 status는
 * 'active'로 남는다. 따라서 노출/자동선택 시점에 `expires_at`(timestamptz)을
 * 전체 시각 기준으로 직접 비교한다. 서버 `validate_coupon_code`의
 * `now() > valid_until` 판정과 의미가 일치한다.
 */
export function isCouponExpired(expiresAt: string): boolean {
  return new Date(expiresAt).getTime() < Date.now();
}

/**
 * 클라이언트 측 할인 금액 미리보기. 서버 권위 검증은 validate_coupon_code로.
 *
 * - fixed: native amount → order currency 변환
 * - percentage: RATE × orderTotal, max (있으면) native → order 변환해서 상한
 */
export function calcCouponDiscount(
  coupon: CouponCalcInput,
  totalPrice: number,
  orderCurrency: SupportedCurrency,
  rates: Partial<ExchangeRates>,
): number {
  const remainder = getMinRemainder(orderCurrency);

  if (coupon.discount_type === "percentage") {
    const rate = coupon.native_discount_value;
    if (rate <= 0) return 0;
    let discount = (totalPrice * rate) / 100;
    if (coupon.native_max_discount && coupon.native_max_discount > 0) {
      const maxOrder = convertCurrency(
        coupon.native_max_discount,
        coupon.native_currency,
        orderCurrency,
        rates,
        COUPON_FX_OPTIONS,
      );
      discount = Math.min(discount, maxOrder);
    }
    return Math.min(
      roundForCurrency(discount, orderCurrency),
      Math.max(0, totalPrice - remainder),
    );
  }

  // fixed
  const converted = convertCurrency(
    coupon.native_discount_value,
    coupon.native_currency,
    orderCurrency,
    rates,
    COUPON_FX_OPTIONS,
  );
  return Math.min(
    roundForCurrency(converted, orderCurrency),
    Math.max(0, totalPrice - remainder),
  );
}

/**
 * 파트너 정산 차감액 계산 — face value와 분리된 파트너 기준 금액.
 *
 * 설계 배경: 기존 `calcCouponDiscount`는 `(items + site_fee)` 전체에 비율을
 * 적용해 고객 face value를 계산한다. 하지만 파트너 정산 관점에서는 "상품가
 * 대비 몇 %"가 자연스러우며, 사이트 수수료 영역에 해당하는 할인 몫은 플랫폼이
 * 흡수해야 파트너가 "98,400원의 10% = 9,840원"을 그대로 본다.
 *
 * 이 함수는 `partnerBase` (items+options, site_fee 제외) 에 대해 동일한
 * rate/max_discount/min_order 로직을 적용한다.
 *
 * - percentage: `partnerBase × rate / 100`, max 상한 적용
 * - fixed: face value와 동일 (max cap = partnerBase)
 *   → 정액 쿠폰이 파트너 매출보다 크면 파트너 매출까지만 차감 (음수 방지)
 */
export function calcCouponPartnerDeduction(
  coupon: CouponCalcInput,
  partnerBase: number,
  orderCurrency: SupportedCurrency,
  rates: Partial<ExchangeRates>,
): number {
  if (partnerBase <= 0) return 0;

  if (coupon.discount_type === "percentage") {
    const rate = coupon.native_discount_value;
    if (rate <= 0) return 0;
    let discount = (partnerBase * rate) / 100;
    if (coupon.native_max_discount && coupon.native_max_discount > 0) {
      const maxOrder = convertCurrency(
        coupon.native_max_discount,
        coupon.native_currency,
        orderCurrency,
        rates,
        COUPON_FX_OPTIONS,
      );
      discount = Math.min(discount, maxOrder);
    }
    return Math.min(roundForCurrency(discount, orderCurrency), partnerBase);
  }

  // fixed — 정액 쿠폰은 face value와 동일하지만 partnerBase 이하로 cap
  const converted = convertCurrency(
    coupon.native_discount_value,
    coupon.native_currency,
    orderCurrency,
    rates,
    COUPON_FX_OPTIONS,
  );
  return Math.min(roundForCurrency(converted, orderCurrency), partnerBase);
}

/**
 * 보유 쿠폰 목록에서 현재 결제 통화/금액 기준 가장 큰 할인 쿠폰 선택.
 */
export function pickBestCoupon(
  list: UserCoupon[],
  totalPrice: number,
  orderCurrency: SupportedCurrency,
  rates: Partial<ExchangeRates>,
): UserCoupon | null {
  if (!areRatesLoaded(rates)) return null; // rates 로드 전엔 선택 불가
  const applicable = list.filter(
    (c) =>
      !isCouponExpired(c.expires_at) &&
      isCouponApplicable(c.coupon, totalPrice, orderCurrency, rates),
  );
  if (applicable.length === 0) return null;
  return applicable.reduce((max, c) =>
    calcCouponDiscount(c.coupon, totalPrice, orderCurrency, rates) >
    calcCouponDiscount(max.coupon, totalPrice, orderCurrency, rates)
      ? c
      : max,
  );
}
